import { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { APP_VERSION_KEY, FORCE_RESET_KEY } from 'constants/localStorage';
import { isPublicRoute } from 'constants/routes';
import { useUpdateModalState } from 'context/UpdateContext';
import resetLocalData, { hasResetVersionThisSession } from 'utils/localDataReset';
import UpdateModal from 'components/UpdateModal';

const CHECK_INTERVAL_MS = 300000; // 5 minutos

const readMetaVersion = () => document.querySelector('meta[name="app-version"]')?.getAttribute('content') || null;

/**
 * Detecta versão nova (via <meta app-version>) e propõe atualização por uma modal
 * adiável, PRESERVANDO a sessão. Substitui o antigo "clear + logout".
 *
 * Comparação por IGUALDADE: em produção a meta é o hash do commit (não-ordenável),
 * então não dá pra usar "mais novo que". Para não entrar em loop quando o HTML vier
 * de cache stale, usa o cinto once-per-session-per-version (hasResetVersionThisSession).
 */
const VersionChecker: FC = () => {
  const [pendingVersion, setPendingVersion] = useState<string | null>(null);
  const { pathname } = useLocation();
  const { setModalUpdateNaTela, reauthNaTela } = useUpdateModalState();

  /* "Modal na tela agora" = tem versão pendente, não é rota pública, e ninguém com
     prioridade maior está interrompendo (MARINERS-2011: sem sessão não há o que atualizar).
     ⚠️ O gate é no RENDER, não na DETECÇÃO. `pendingVersion` continua sendo setado e
     preservado durante a re-autenticação — então, quando ela resolve, a modal de update
     aparece no mesmo instante, em vez de o usuário ficar sem o aviso até o próximo tick
     de 5 minutos do checker. Gatear a detecção seria mais simples de escrever e engoliria
     o aviso. */
  const modalOnScreen = pendingVersion !== null && !isPublicRoute(pathname) && !reauthNaTela;

  // Publica o flag ANTES do paint (layout effect, não useEffect pós-paint) — pro Amplifique
  // enxergar "modal na tela" no mesmo ciclo em que a modal aparece/some.
  useLayoutEffect(() => {
    setModalUpdateNaTela(modalOnScreen);
  }, [modalOnScreen, setModalUpdateNaTela]);

  const checkVersion = useCallback(() => {
    // Não incomoda em rota pública (QR/chamado anônimo, login/logout) — fonte única.
    if (isPublicRoute(pathname)) return;

    const currentVersion = readMetaVersion();
    if (!currentVersion) return; // sem meta, não há como comparar

    const lastKnownVersion = localStorage.getItem(APP_VERSION_KEY);
    const hasReset = localStorage.getItem(FORCE_RESET_KEY);

    // Primeira visita (ou storage recém-limpo): só registra o baseline, não propõe update.
    if (!lastKnownVersion) {
      localStorage.setItem(APP_VERSION_KEY, currentVersion);
      if (!hasReset) localStorage.setItem(FORCE_RESET_KEY, 'true');
      return;
    }

    // Mesma versão → nada a fazer.
    if (lastKnownVersion === currentVersion) return;

    // Cinto once-per-session-per-version: já resetamos para esta versão nesta sessão
    // e o mismatch persiste (HTML de cache stale) → não reabre a modal.
    if (hasResetVersionThisSession(currentVersion)) return;

    // Mismatch real → propõe atualização (modal adiável), sem deslogar.
    setPendingVersion(currentVersion);
  }, [pathname]);

  useEffect(() => {
    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkVersion]);

  const handleConfirm = useCallback(() => {
    Sentry?.logger?.info?.('[VersionChecker] usuário confirmou atualização', { pendingVersion });
    // Limpa o cache local preservando o login, marca a sessão e recarrega.
    resetLocalData(pendingVersion);
  }, [pendingVersion]);

  const handleDismiss = useCallback(() => {
    // Adiável: fecha; re-lembra no próximo load ou no próximo tick de 5 min.
    setPendingVersion(null);
  }, []);

  // Não mostra em rota pública nem sem versão pendente (modalOnScreen já cobre os dois).
  if (!modalOnScreen) return null;

  return <UpdateModal onConfirm={handleConfirm} onDismiss={handleDismiss} />;
};

export default VersionChecker;
